import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const NAV_ITEMS = [
  { path:'/',                      label:'Dashboard',        icon:'layout-dashboard', admin:false },
  { path:'/calculator',            label:'Calculator',       icon:'calculator',    admin:false },
  { path:'/comparison',            label:'Compare',          icon:'arrows-diff',   admin:false, feature:'comparison' },
  { path:'/calculations',          label:'Saved Quotes',     icon:'history',       admin:false },
  { path:'/subscription',          label:'My Subscription',  icon:'credit-card',   admin:false, orgAdmin:true },
  { divider:true, label:'Admin',                                                   admin:true  },
  { path:'/admin/pricing',         label:'Pricing',          icon:'tag',           admin:true  },
  { path:'/admin/orgs',            label:'Organisations',    icon:'building',      admin:true  },
  { path:'/admin/users',           label:'Users',            icon:'users',         admin:true  },
  { path:'/admin/currencies',      label:'Currencies',       icon:'currency-dollar',admin:true },
  { path:'/admin/insurance',       label:'Medical Insurance',icon:'heart-plus',    admin:true  },
  { path:'/admin/country-config',  label:'Country Config',   icon:'settings-2',    admin:true  },
  { path:'/admin/custom-burdens',  label:'Custom Burdens',   icon:'plus',          admin:true  },
];

const QuoteologyLogo = ({ size = 36 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Quoteology" style={{flexShrink:0}}>
    <circle cx="28" cy="28" r="25" fill="none" stroke="#3FD39A" strokeOpacity="0.35" strokeWidth="1.6"/>
    <g>
      <circle cx="53" cy="28" r="4.2" fill="#3FD39A"/>
      <animateTransform attributeName="transform" attributeType="XML" type="rotate"
        from="0 28 28" to="360 28 28" dur="5s" repeatCount="indefinite"/>
    </g>
    <circle cx="28" cy="28" r="20" fill="none" stroke="#FFFFFF" strokeWidth="6.5"/>
    <rect x="18.6" y="31" width="3.2" height="6"  rx="1.2" fill="#FFFFFF"/>
    <rect x="23.8" y="26" width="3.2" height="11" rx="1.2" fill="#FFFFFF"/>
    <rect x="29"   y="22" width="3.2" height="15" rx="1.2" fill="#FFFFFF"/>
    <rect x="34.2" y="17" width="3.2" height="20" rx="1.2" fill="#3FD39A"/>
    <line x1="40" y1="40" x2="52" y2="52" stroke="#FFFFFF" strokeWidth="6.5" strokeLinecap="round"/>
  </svg>
);

const isMobileVP = () => window.innerWidth <= 768;

export default function Layout({ children }) {
  const { user, logout, isAdmin, hasFeature } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [collapsed,    setCollapsed]    = useState(false);
  const [isMobile,     setIsMobile]     = useState(isMobileVP());
  const [drawerOpen,   setDrawerOpen]   = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(isMobileVP());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => { setDrawerOpen(false); setUserMenuOpen(false); }, [location.pathname]);

  const handleLogout = () => { logout(); toast.success('Signed out'); navigate('/login'); };
  const items = NAV_ITEMS.filter(n => {
    if (n.admin && !isAdmin) return false;
    if (n.orgAdmin && (!user?.org_id || user?.role !== 'admin')) return false;
    if (n.feature && !hasFeature(n.feature)) return false;
    return true;
  });
  const W = isMobile ? 0 : (collapsed ? 64 : 232);

  const SIDEBAR_BG  = '#0C1220';
  const ACTIVE_BG   = 'rgba(14,165,233,.15)';
  const ACTIVE_BAR  = '#0EA5E9';
  const ICON_COLOR  = 'rgba(255,255,255,.45)';
  const LABEL_COLOR = 'rgba(255,255,255,.65)';

  const SidebarContent = () => (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>

      {/* Brand */}
      <div style={{
        padding: (collapsed && !isMobile) ? '16px 0' : '16px',
        borderBottom:'1px solid rgba(255,255,255,.08)',
        display:'flex',alignItems:'center',gap:10,flexShrink:0,
      }}>
        <div style={{
          margin:(collapsed && !isMobile) ? '0 auto' : 0,
          background:'rgba(14,165,233,.18)',
          borderRadius:10,padding:4,display:'flex',flexShrink:0,
        }}>
          <QuoteologyLogo size={28}/>
        </div>

        {(!collapsed || isMobile) && (
          <div style={{overflow:'hidden'}}>
            <div style={{color:'#fff',fontWeight:700,fontSize:15,letterSpacing:'-.3px',lineHeight:1.1}}>Quoteology</div>
            <div style={{color:'rgba(255,255,255,.35)',fontSize:9.5,letterSpacing:'.08em',textTransform:'uppercase',marginTop:2}}>Cost Platform</div>
          </div>
        )}

        {isMobile && (
          <button onClick={() => setDrawerOpen(false)} style={{marginLeft:'auto',background:'none',border:'none',color:'rgba(255,255,255,.5)',fontSize:18,cursor:'pointer',padding:'0 4px',lineHeight:1}}>✕</button>
        )}
      </div>

      {/* Nav */}
      <nav style={{flex:1,padding:'8px 0',overflowY:'auto'}}>
        {items.map((item, i) => {
          if (item.divider) return (!collapsed || isMobile)
            ? <div key={i} style={{padding:'16px 16px 4px',fontSize:9.5,fontWeight:700,color:'rgba(255,255,255,.25)',textTransform:'uppercase',letterSpacing:'.1em'}}>{item.label}</div>
            : <div key={i} style={{height:'1px',background:'rgba(255,255,255,.07)',margin:'8px 12px'}} />;

          return (
            <NavLink key={item.path} to={item.path} end={item.path === '/'}
              style={({ isActive }) => ({
                display:'flex',alignItems:'center',gap:10,
                padding:(collapsed && !isMobile) ? '10px 0' : '9px 14px',
                justifyContent:(collapsed && !isMobile) ? 'center' : 'flex-start',
                color: isActive ? '#fff' : LABEL_COLOR,
                textDecoration:'none',fontSize:13,fontWeight: isActive ? 600 : 400,
                background: isActive ? ACTIVE_BG : 'transparent',
                borderLeft: isActive ? `3px solid ${ACTIVE_BAR}` : '3px solid transparent',
                transition:'all .12s',marginBottom:1,
              })}>
              <i className={`ti ti-${item.icon}`} style={{fontSize:16,color: 'inherit',opacity: .8,flexShrink:0}} aria-hidden="true"/>
              {(!collapsed || isMobile) && <span style={{fontSize:13}}>{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{borderTop:'1px solid rgba(255,255,255,.08)',padding:(collapsed && !isMobile) ? '12px 0' : '12px 14px',flexShrink:0,position:'relative'}}>

        {/* User avatar — clickable dropdown */}
        {(!collapsed || isMobile) && (
          <div style={{marginBottom:10,position:'relative'}}>
            <div
              onClick={() => setUserMenuOpen(o => !o)}
              style={{
                display:'flex',alignItems:'center',gap:9,cursor:'pointer',
                padding:'6px 8px',borderRadius:8,
                background: userMenuOpen ? 'rgba(255,255,255,.08)' : 'transparent',
                transition:'background .12s',
              }}
              onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,.08)'}
              onMouseLeave={e => { if(!userMenuOpen) e.currentTarget.style.background='transparent'; }}
            >
              <div style={{width:30,height:30,borderRadius:'50%',background:'rgba(14,165,233,.25)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,color:'#38BDF8',flexShrink:0}}>
                {user?.name?.[0]?.toUpperCase()}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12.5,fontWeight:600,color:'#fff',lineHeight:1.2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{user?.name}</div>
                <div style={{fontSize:10.5,color:'rgba(255,255,255,.35)',textTransform:'capitalize'}}>{user?.role}</div>
              </div>
              <i className="ti ti-chevron-up" style={{fontSize:12,color:'rgba(255,255,255,.35)',transform:userMenuOpen?'rotate(0deg)':'rotate(180deg)',transition:'transform .2s'}} aria-hidden="true"/>
            </div>

            {userMenuOpen && (
              <div style={{
                position:'absolute',bottom:'100%',left:0,right:0,marginBottom:4,
                background:'#1a2332',border:'1px solid rgba(255,255,255,.1)',
                borderRadius:8,overflow:'hidden',
                boxShadow:'0 -8px 24px rgba(0,0,0,.3)',zIndex:200,
              }}>
                {user?.org_id && (
                  <button onClick={() => { setUserMenuOpen(false); navigate('/subscription'); }}
                    style={{width:'100%',display:'flex',alignItems:'center',gap:9,padding:'10px 14px',background:'transparent',border:'none',color:'rgba(255,255,255,.7)',fontSize:13,cursor:'pointer',textAlign:'left',fontFamily:'inherit'}}
                    onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,.07)'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                    <i className="ti ti-chart-bar" style={{fontSize:15,color:'#38BDF8'}} aria-hidden="true"/>
                    Usage details
                  </button>
                )}
                {user?.org_id && (
                  <button onClick={() => { setUserMenuOpen(false); navigate('/plans'); }}
                    style={{width:'100%',display:'flex',alignItems:'center',gap:9,padding:'10px 14px',background:'transparent',border:'none',color:'rgba(255,255,255,.7)',fontSize:13,cursor:'pointer',textAlign:'left',fontFamily:'inherit'}}
                    onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,.07)'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                    <i className="ti ti-arrow-up-circle" style={{fontSize:15,color:'#38BDF8'}} aria-hidden="true"/>
                    View plans
                  </button>
                )}
                <div style={{height:'1px',background:'rgba(255,255,255,.07)',margin:'2px 0'}}/>
                <button onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                  style={{width:'100%',display:'flex',alignItems:'center',gap:9,padding:'10px 14px',background:'transparent',border:'none',color:'#FCA5A5',fontSize:13,cursor:'pointer',textAlign:'left',fontFamily:'inherit'}}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(239,68,68,.1)'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                  <i className="ti ti-logout" style={{fontSize:15}} aria-hidden="true"/>
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}

        <div style={{display:'flex',gap:6,justifyContent:(collapsed && !isMobile) ? 'center' : 'flex-end'}}>
          {!isMobile && (
            <button onClick={() => setCollapsed(c => !c)}
              style={{background:'rgba(255,255,255,.08)',border:'none',color:'rgba(255,255,255,.6)',borderRadius:6,padding:'6px 10px',cursor:'pointer',fontSize:12,transition:'background .12s'}}
              onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,.13)'}
              onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,.08)'}>
              {collapsed ? '→' : '←'}
            </button>
          )}
          {(collapsed && !isMobile) && (
            <button onClick={handleLogout}
              style={{background:'rgba(239,68,68,.15)',color:'#FCA5A5',border:'1px solid rgba(239,68,68,.2)',borderRadius:6,padding:'6px 10px',cursor:'pointer',fontSize:12}}
              onMouseEnter={e => e.currentTarget.style.background='rgba(239,68,68,.25)'}
              onMouseLeave={e => e.currentTarget.style.background='rgba(239,68,68,.15)'}>
              <i className="ti ti-logout" style={{fontSize:14}} aria-hidden="true"/>
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{display:'flex',minHeight:'100vh'}}>

      {/* Desktop sidebar */}
      {!isMobile && (
        <aside style={{
          width:W,background:SIDEBAR_BG,
          display:'flex',flexDirection:'column',
          position:'fixed',top:0,bottom:0,left:0,zIndex:100,
          transition:'width .2s ease',overflow:'hidden',
          borderRight:'1px solid rgba(255,255,255,.05)',
        }}>
          <SidebarContent />
        </aside>
      )}

      {/* Mobile drawer */}
      {isMobile && drawerOpen && (
        <div style={{position:'fixed',inset:0,zIndex:200}}>
          <div onClick={() => setDrawerOpen(false)} style={{position:'absolute',inset:0,background:'rgba(0,0,0,.5)',backdropFilter:'blur(2px)'}}/>
          <aside style={{position:'absolute',top:0,left:0,bottom:0,width:240,background:SIDEBAR_BG,display:'flex',flexDirection:'column',zIndex:201}}>
            <SidebarContent/>
          </aside>
        </div>
      )}

      {/* Main */}
      <main style={{marginLeft:W,flex:1,transition:'margin-left .2s ease',minWidth:0,display:'flex',flexDirection:'column',background:'#F1F5F9',minHeight:'100vh'}}>

        {/* Mobile top bar — sticky so always visible regardless of browser chrome */}
        {isMobile && (
          <div style={{
            background:SIDEBAR_BG,
            padding:'10px 16px',
            display:'flex',alignItems:'center',gap:12,
            flexShrink:0,
            borderBottom:'1px solid rgba(255,255,255,.07)',
            position:'sticky',
            top:0,
            zIndex:50,
          }}>
            <button onClick={() => setDrawerOpen(true)} style={{background:'rgba(255,255,255,.1)',border:'none',color:'#fff',borderRadius:6,padding:'7px 10px',cursor:'pointer',fontSize:15,lineHeight:1}}>
              <i className="ti ti-menu-2" aria-hidden="true"/>
            </button>
            <div style={{background:'rgba(14,165,233,.18)',borderRadius:8,padding:3,display:'flex'}}>
              <QuoteologyLogo size={24}/>
            </div>
            <div style={{color:'#fff',fontWeight:700,fontSize:14,letterSpacing:'-.2px'}}>Quoteology</div>
          </div>
        )}

        {/* Page content */}
        <div style={{flex:1,padding: isMobile ? '16px' : '24px 28px'}}>
          {children}
        </div>
      </main>
    </div>
  );
}